<template>
  <v-row align="center" justify="center">
    <v-col cols="12" class="d-flex justify-center">
      <v-card style="width: 800px" shaped>
        <v-card-title>
          <v-icon large left color="primary">mdi-chart-line</v-icon>
          <span class="title font-weight-light">Тренд продаж дистрибьюторов</span>
        </v-card-title>
        <v-form ref="form">
          <v-col class="d-flex">
            <v-select
              class="mx-2"
              label="Период"
              item-text="name"
              item-value="id"
              :items="options.periods"
              :loading="loading.options"
              v-model="periodId"
              persistent-hint
              :hint="periodHint"
            ></v-select>
            <v-select
              class="mx-2"
              label="Тип диаграммы"
              :items="chartTypeItems"
              :loading="loading.options"
              v-model="chartType"
              :rules="[rules.required]"
            ></v-select>
          </v-col>
          <v-col class="d-flex">
            <v-text-field
              class="mx-2"
              v-model="dateStart"
              label="Дата от... (включительно)"
              type="date"
              :rules="[rules.required]"
            ></v-text-field>
            <v-text-field
              class="mx-2"
              v-model="dateEnd"
              label="Дата до... (включительно)"
              type="date"
              :rules="[rules.required]"
            ></v-text-field>
          </v-col>
          <v-col class="d-flex">
            <v-autocomplete
              class="mx-2"
              label="Регион"
              item-text="name"
              item-value="id"
              :items="cityItems"
              :loading="loading.options"
              v-model="cityId"
              persistent-hint
              hint="Весь регион — сравнение дистрибьюторов по всему Казахстану"
            ></v-autocomplete>
            <v-autocomplete
              v-if="isRegionMode"
              class="mx-2"
              label="Свой филиал"
              item-text="name"
              item-value="id"
              :items="ownFilialItems"
              :loading="loading.options"
              v-model="filialId"
              :rules="[rules.requiredId]"
              persistent-hint
              hint="Ваш филиал в выбранном регионе"
            ></v-autocomplete>
          </v-col>
          <v-col class="d-flex">
            <v-autocomplete
              class="mx-2"
              :label="competitorsLabel"
              item-text="name"
              item-value="id"
              :items="competitorItems"
              :loading="loading.options"
              v-model="competitorIds"
              :rules="competitorRules"
              multiple
              chips
              small-chips
              deletable-chips
              clearable
              persistent-hint
              :hint="competitorsHint"
            ></v-autocomplete>
          </v-col>
          <v-col class="d-flex">
            <v-btn
              :loading="loading.download"
              @click="DownloadReport()"
              block
              color="primary"
            >
              Сформировать отчёт
              <template v-slot:loader>
                <span>Отчёт формируется...</span>
              </template>
            </v-btn>
          </v-col>
        </v-form>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
const ALL_REGIONS_ID = 0;
const NOT_SELECTED_ID = 0;

export default {
  data: () => ({
    periodId: 0,
    chartType: null,
    dateStart: null,
    dateEnd: null,
    cityId: ALL_REGIONS_ID,
    filialId: NOT_SELECTED_ID,
    competitorIds: [],
    options: {
      periods: [],
      cities: [],
      ownFilials: [],
      distributors: [],
      filials: [],
      chartTypeGroups: [],
      defaultChartType: null,
      minCompetitorCount: 5,
      maxCompetitorCount: 10,
    },
    loading: {
      options: true,
      download: false,
    },
    rules: {
      required: (value) => !!value || "Поле обязательно для заполнения.",
      requiredId: (value) => value > 0 || "Поле обязательно для заполнения.",
    },
  }),
  computed: {
    isRegionMode() {
      return this.cityId > ALL_REGIONS_ID;
    },
    periodHint() {
      const period = this.options.periods.find((x) => x.id === this.periodId);
      return period ? period.hint : "";
    },
    cityItems() {
      return [{ id: ALL_REGIONS_ID, name: "Весь регион" }, ...this.options.cities];
    },
    ownFilialItems() {
      return this.options.ownFilials.filter((x) => x.cityId === this.cityId);
    },
    competitorItems() {
      return this.isRegionMode
        ? this.options.filials.filter((x) => x.cityId === this.cityId)
        : this.options.distributors;
    },
    competitorsLabel() {
      return this.isRegionMode ? "Филиалы-конкуренты" : "Дистрибьюторы-конкуренты";
    },
    competitorsHint() {
      return `Выберите от ${this.options.minCompetitorCount} до ${this.options.maxCompetitorCount}; в отчёте они обезличены`;
    },
    // Заголовки групп и разделитель понимает v-select Vuetify
    chartTypeItems() {
      const items = [];
      this.options.chartTypeGroups.forEach((group, index) => {
        if (index > 0) {
          items.push({ divider: true });
        }
        items.push({ header: group.name });
        group.types.forEach((type) => {
          items.push({ text: `Диаграмма - "${type}"`, value: type });
        });
      });
      return items;
    },
    competitorRules() {
      return [
        (value) =>
          (Array.isArray(value) &&
            value.length >= this.options.minCompetitorCount &&
            value.length <= this.options.maxCompetitorCount) ||
          `Выберите от ${this.options.minCompetitorCount} до ${this.options.maxCompetitorCount} конкурентов`,
      ];
    },
  },
  mounted() {
    this.GetFilterOptions();
  },
  methods: {
    async GetFilterOptions() {
      this.loading.options = true;
      try {
        const options = await this.$store.dispatch("SALES_TREND_REPORT_FILTER_OPTIONS");
        this.options = options;
        this.chartType = options.defaultChartType;
        if (options.periods.length > 0) {
          this.periodId = options.periods[0].id;
        }
      } catch (error) {
        window.console.log(error);
      } finally {
        this.loading.options = false;
      }
    },
    DownloadReport() {
      if (!this.$refs.form.validate()) {
        return;
      }

      const request = {
        period: this.periodId,
        dateStart: this.dateStart,
        dateEnd: this.dateEnd,
        cityId: this.cityId,
        filialId: this.isRegionMode ? this.filialId : NOT_SELECTED_ID,
        competitorIds: this.competitorIds,
        chartType: this.chartType,
      };

      this.loading.download = true;
      this.$store
        .dispatch("DOWNLOAD_SALES_TREND_REPORT", request)
        .then((hasData) => {
          this.loading.download = false;
          if (hasData === false) {
            this.$store.commit("SET_SNACKBAR", {
              text: "За выбранный период нет продаж",
              timeout: 7500,
              color: "warning",
              linkText: "",
              show: true,
            });
          }
        })
        .catch((error) => {
          this.loading.download = false;
          window.console.log(error);
        });
    },
  },
  watch: {
    cityId() {
      this.filialId = NOT_SELECTED_ID;
      this.competitorIds = [];
    },
  },
};
</script>
